import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCreateLifestyleGalleryItem, useUpdateLifestyleGalleryItem, LifestyleGalleryItem } from '@/hooks/useLifestyleGallery';

const lifestyleGallerySchema = z.object({
  title: z.string().min(1, 'Title is required').max(40, 'Title must be 40 characters or less'),
  category: z.string().min(1, 'Category is required').max(30, 'Category must be 30 characters or less'),
  image_url: z.string().url('Please enter a valid image URL'),
  likes: z.number().min(0, 'Likes must be 0 or greater'),
  views: z.number().min(0, 'Views must be 0 or greater'),
  is_active: z.boolean(),
  sort_order: z.number().min(0, 'Sort order must be 0 or greater'),
});

type LifestyleGalleryFormData = z.infer<typeof lifestyleGallerySchema>;

interface LifestyleGalleryFormProps {
  item?: LifestyleGalleryItem | null;
  onClose: () => void;
}

export const LifestyleGalleryForm: React.FC<LifestyleGalleryFormProps> = ({ item, onClose }) => {
  const [titleLength, setTitleLength] = useState(0);
  const [categoryLength, setCategoryLength] = useState(0);

  const createMutation = useCreateLifestyleGalleryItem();
  const updateMutation = useUpdateLifestyleGalleryItem();

  const form = useForm<LifestyleGalleryFormData>({
    resolver: zodResolver(lifestyleGallerySchema),
    defaultValues: {
      title: '',
      category: '',
      image_url: '',
      likes: 0,
      views: 0,
      is_active: true,
      sort_order: 0,
    },
  });

  useEffect(() => {
    if (item) {
      form.reset({
        title: item.title,
        category: item.category,
        image_url: item.image_url,
        likes: item.likes,
        views: item.views,
        is_active: item.is_active,
        sort_order: item.sort_order,
      });
      setTitleLength(item.title.length);
      setCategoryLength(item.category.length);
    }
  }, [item, form]);

  const onSubmit = (data: LifestyleGalleryFormData) => {
    if (item) {
      updateMutation.mutate(
        { id: item.id, data: data as any },
        {
          onSuccess: onClose,
        }
      );
    } else {
      createMutation.mutate(data as any, {
        onSuccess: onClose,
      });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {item ? 'Edit Gallery Item' : 'Add Gallery Item'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter image title"
                        onChange={(e) => {
                          field.onChange(e);
                          setTitleLength(e.target.value.length);
                        }}
                      />
                    </FormControl>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <FormMessage />
                      <span>{titleLength}/40</span>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., Interior, Lifestyle"
                        onChange={(e) => {
                          field.onChange(e);
                          setCategoryLength(e.target.value.length);
                        }}
                      />
                    </FormControl>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <FormMessage />
                      <span>{categoryLength}/30</span>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Image URL *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="https://example.com/image.jpg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="likes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Likes</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={0}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="views"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Views</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={0}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={0}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Show this image in the gallery
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : item ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};